import type { Recommendation } from '../../types';

interface ScoreBadgeProps {
  score: number;
  type?: 'score' | 'grade';
  variant?: 'investment' | 'self'; // investment = default, self = personal residence
  size?: 'sm' | 'md'; // sm = small for table cells, md = default
}

interface RecommendationBadgeProps {
  recommendation: Recommendation;
}

export function ScoreBadge({ score, type = 'score', variant = 'investment', size = 'md' }: ScoreBadgeProps) {
  const getScoreClass = (s: number) => {
    if (s >= 70) return 'score-high';
    if (s >= 50) return 'score-medium';
    return 'score-low';
  };

  if (type === 'grade') {
    const grade = getGrade(score);
    const gradeClass = 'grade-' + grade.replace('+', 'plus');
    return <span className={`grade-badge ${gradeClass}`}>{grade}</span>;
  }

  // Self score uses a different style (blue tint for personal residence)
  const variantClass = variant === 'self' ? 'score-self' : '';
  const sizeClass = size === 'sm' ? 'score-sm' : '';

  return (
    <span className={`property-score ${getScoreClass(score)} ${variantClass} ${sizeClass}`} title={variant === 'self' ? 'Personal Residence Score' : 'Investment Score'}>
      {Math.round(score)}
    </span>
  );
}

export function RecommendationBadge({ recommendation }: RecommendationBadgeProps) {
  const recClass = getRecommendationClass(recommendation);
  return (
    <span className={`property-rec ${recClass}`}>
      {recommendation}
    </span>
  );
}

function getGrade(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 75) return 'B+';
  if (score >= 65) return 'B';
  if (score >= 55) return 'C+';
  if (score >= 45) return 'C';
  return 'D';
}

function getRecommendationClass(rec: Recommendation): string {
  const map: Record<Recommendation, string> = {
    'STRONG BUY': 'rec-strong-buy',
    'BUY': 'rec-buy',
    'CONSIDER': 'rec-consider',
    'HOLD': 'rec-hold',
    'PASS': 'rec-pass',
  };
  return map[rec] || 'rec-hold';
}
